import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  resetStatus,
} from "../../../slice/adminUser/adminUser";

// ─── Role mapping: numeric (DB) ↔ display label ───────────────────────────
const ROLE_OPTIONS = [
  { value: 1, label: "Super Admin" },
  { value: 2, label: "Admin" },
  { value: 3, label: "Operator" },
];

const getRoleLabel = (roleNum) =>
  ROLE_OPTIONS.find((r) => r.value === Number(roleNum))?.label ?? "Unknown";

const getRoleBadgeStyle = (roleNum) => {
  switch (Number(roleNum)) {
    case 1: return "bg-primary";
    case 2: return "bg-secondary";
    case 3: return "bg-success";
    default: return "bg-secondary";
  }
};

// ─── Component ─────────────────────────────────────────────────────────────
const Adduser = () => {
  const tableRef = useRef(null);
  const dispatch = useDispatch();

  // ── Logged-in user's role from localStorage ──
  const loggedInRole = Number(localStorage.getItem("role"));

  // ── Roles this user is allowed to create ──
  const creatableRoles = ROLE_OPTIONS.filter((r) => {
    if (loggedInRole === 1) return r.value === 2 || r.value === 3; // Super Admin → Admin + Operator
    if (loggedInRole === 2) return r.value === 3;                  // Admin → Operator only
    return false;                                                   // Operator → none
  });

  const { list: users, loading, success, error } = useSelector(
    (state) => state.adminUser
  );

  // ── Search state ──
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  // Client-side filtered list
  const filteredUsers = users.filter((u) =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Modal state ──
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newUsername, setNewUsername] = useState("");
  const [newRole, setNewRole] = useState(creatableRoles[0]?.value ?? 3);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // ── Fetch users on mount ──
  useEffect(() => {
    dispatch(fetchAdminUsers());
  }, [dispatch]);

  // ── Close modal & re-fetch on successful create/update ──
  useEffect(() => {
    if (success) {
      dispatch(fetchAdminUsers());
      dispatch(resetStatus());
      handleClose();
    }
  }, [success]);

  // ── DataTable init (re-run when filtered list changes) ──
  useEffect(() => {
    const init = () => {
      if (window.$?.fn?.DataTable) {
        if (window.$.fn.DataTable.isDataTable(tableRef.current)) {
          window.$(tableRef.current).DataTable().destroy();
        }
        window.$(tableRef.current).DataTable({
          responsive: true,
          pageLength: 10,
          columnDefs: [{ orderable: false, targets: [0, 4] }],
        });
      } else {
        setTimeout(init, 300);
      }
    };
    init();
    return () => {
      if (window.$?.fn?.DataTable?.isDataTable?.(tableRef.current)) {
        window.$(tableRef.current).DataTable().destroy();
      }
    };
  }, [filteredUsers]);

  // ── Handlers ──
  const handleDelete = (id) => {
    if (window.confirm("Delete this user?")) {
      dispatch(deleteAdminUser(id));
    }
  };

  const openAddModal = () => {
    setEditId(null);
    setNewUsername("");
    setNewRole(creatableRoles[0]?.value ?? 3); // default to first allowed role
    setNewPassword("");
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditId(user._id);
    setNewUsername(user.username);
    setNewRole(Number(user.role));
    setNewPassword("");
    setShowModal(true);
  };

  const handleSave = () => {
    if (!newUsername.trim()) return alert("Enter username");
    if (!newPassword && !editId) return alert("Enter password");

    // Guard against privilege escalation
    const isAllowed = creatableRoles.some((r) => r.value === Number(newRole));
    if (!isAllowed) return alert("You don't have permission to assign this role.");

    const payload = {
      username: newUsername.trim(),
      role: Number(newRole),
      ...(newPassword ? { password: newPassword } : {}),
    };

    if (editId) {
      dispatch(updateAdminUser({ id: editId, data: payload }));
    } else {
      dispatch(createAdminUser({ ...payload, password: newPassword }));
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setEditId(null);
    setNewUsername("");
    setNewRole(creatableRoles[0]?.value ?? 3);
    setNewPassword("");
    setShowPassword(false);
    dispatch(resetStatus());
  };

  // ── Render ──
  return (
    <div>
      {/* TABLE CARD */}
      <div className="container-fluid">
        <div className="card"  style={{
            minHeight:
              "calc(100vh - 72px)",
          }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="job-filter">
              <div className="faq-form">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search.."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24" height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-search search-icon"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
            </div>

            {/* Only show Add button if this role can create someone */}
            {creatableRoles.length > 0 && (
              <button
                className="btn btn-primary btn-sm widthbtn"
                onClick={openAddModal}
              >
                + Add
              </button>
            )}
          </div>

          <div className="card-body">
            {loading && <p className="text-muted">Loading users…</p>}
            {error && <p className="text-danger">{error}</p>}

            <table
              className="display table table-bordered"
              ref={tableRef}
              style={{ width: "100%" }}
            >
              <thead>
                <tr>
                  <th>SI No</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={user._id}>
                    <td>{index + 1}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 36, height: 36, borderRadius: "50%",
                            background: Number(user.role) === 1 ? "#6362e7" : "#10b981",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "white", fontWeight: "bold",
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <span>{user.username}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${getRoleBadgeStyle(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td style={{ display: "flex", gap: 8 }}>
                      {/* Edit — only show if logged-in user can manage this user's role */}
                      {creatableRoles.some((r) => r.value === Number(user.role)) && (
                        <button
                          onClick={() => handleEdit(user)}
                          title="Edit"
                          style={{
                            width: 36, height: 36, borderRadius: 8, border: "none",
                            background: "#eff6ff", color: "#6362e7", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#dbeafe")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#eff6ff")}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15" height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}

                      {/* Delete — only show if logged-in user can manage this user's role */}
                      {creatableRoles.some((r) => r.value === Number(user.role)) && (
                        <button
                          onClick={() => handleDelete(user._id)}
                          title="Delete"
                          style={{
                            width: 36, height: 36, borderRadius: 8, border: "none",
                            background: "#fff5f5", color: "#ef4444", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#fee2e2")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff5f5")}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15" height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" /><path d="M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div
            onClick={handleClose}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />

          {/* Modal Box */}
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050, width: "100%", maxWidth: 520,
              background: "#fff", borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "32px 32px 28px",
              fontFamily: "'Segoe UI', sans-serif",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex", alignItems: "center",
                justifyContent: "space-between", marginBottom: 28,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#111" }}>
                {editId ? "Edit User" : "Add New User"}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    background: "#eff6ff", color: "#6362e7",
                    fontSize: 11, fontWeight: 700, letterSpacing: 1,
                    padding: "4px 12px", borderRadius: 20,
                    border: "1px solid #6362e7",
                  }}
                >
                  {editId ? "EDITING" : "ADDING"}
                </span>
                <button
                  onClick={handleClose}
                  style={{
                    width: 32, height: 32, borderRadius: "50%",
                    border: "1px solid #e5e7eb", background: "#f9fafb",
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 16, color: "#555",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <hr style={{ borderColor: "#f0f0f0", marginBottom: 24 }} />

            {/* Error banner */}
            {error && (
              <div
                style={{
                  background: "#fff5f5", color: "#ef4444",
                  padding: "10px 14px", borderRadius: 8,
                  fontSize: 13, marginBottom: 16,
                  border: "1px solid #fecaca",
                }}
              >
                {error}
              </div>
            )}

            {/* Username */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>
                Username / Email <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="e.g. john@example.com or johndoe"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            {/* Role — only show allowed roles */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>
                Role <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(Number(e.target.value))}
                style={{ ...inputStyle, backgroundColor: "#fff", cursor: "pointer" }}
                onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              >
                {creatableRoles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Password */}
            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>
                Password{" "}
                {!editId ? (
                  <span style={{ color: "#ef4444" }}>*</span>
                ) : (
                  <span style={{ color: "#888", fontSize: 10, fontWeight: "normal" }}>
                    {" "}(Leave blank to keep current)
                  </span>
                )}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={editId ? "Enter new password (optional)" : "Enter password"}
                  style={{ ...inputStyle, paddingRight: 45 }}
                  onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute", right: 12, top: "50%",
                    transform: "translateY(-50%)",
                    background: "none", border: "none",
                    cursor: "pointer", fontSize: 16, color: "#888",
                    display: "flex", alignItems: "center",
                  }}
                >
                  {showPassword ? "👁️" : "🔒"}
                </button>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={handleClose}
                style={cancelBtnStyle}
                onMouseOver={(e) => (e.target.style.background = "#f0f0f0")}
                onMouseOut={(e) => (e.target.style.background = "#f9fafb")}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                style={{ ...saveBtnStyle, opacity: loading ? 0.7 : 1 }}
                onMouseOver={(e) => !loading && (e.target.style.background = "#4f4fd0")}
                onMouseOut={(e) => !loading && (e.target.style.background = "#6362e7")}
              >
                {loading ? "Saving…" : editId ? "Update User" : "Save User"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Shared styles ──────────────────────────────────────────────────────────
const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 700,
  letterSpacing: 1, color: "#888", marginBottom: 8,
  textTransform: "uppercase",
};

const inputStyle = {
  width: "100%", padding: "12px 16px", borderRadius: 10,
  border: "1.5px solid #e5e7eb", fontSize: 14, color: "#333",
  outline: "none", boxSizing: "border-box", transition: "border-color 0.2s",
};

const cancelBtnStyle = {
  flex: "0 0 auto", padding: "12px 28px", borderRadius: 10,
  border: "1.5px solid #e5e7eb", background: "#f9fafb",
  color: "#555", fontWeight: 600, fontSize: 14,
  cursor: "pointer", transition: "background 0.2s",
};

const saveBtnStyle = {
  flex: 1, padding: "12px 28px", borderRadius: 10,
  border: "none", background: "#6362e7", color: "#fff",
  fontWeight: 700, fontSize: 14, cursor: "pointer",
  letterSpacing: 0.3, transition: "background 0.2s",
};

export default Adduser;